import type {
  CabinetApi, TodayView, DomainId, DomainView, OpsFeed, OpsKind, MemoryView, RecallResponse, HealthInfo, ChatSummary, ChatMessage,
  UsageView, UsageRollingView, PerfView,
  MoneySummary, MoneyTransaction, MoneyTrend, CategorySpend, Holding, FinancialAccount, NetWorth,
  SettingView,
} from './contracts.js';

/* The real client: fetches the gateway endpoints the contracts define. The
   server side lands in A5 (stubs) → A11 (real). Surfaces never see this
   directly — they depend on CabinetApi. */

class AuthRequiredError extends Error { constructor() { super('auth required'); } }

/**
 * A non-2xx response, carrying the parts of the body a surface can actually
 * act on. `status` and `code` survive separately because a surface routinely
 * needs to branch on them — a 409 refusing a change is a different rendering
 * from a 400 rejecting a format — and neither survives being flattened into a
 * message string. `message` still reads sensibly for every caller that only
 * ever looks at `e.message`.
 */
class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly code: string | null = null,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function fail(r: Response): Promise<never> {
  const text = await r.text();
  let body: Record<string, unknown> | null = null;
  try {
    const parsed: unknown = JSON.parse(text);
    if (parsed && typeof parsed === 'object') body = parsed as Record<string, unknown>;
  } catch {
    /* not JSON — fall through to the raw text below */
  }
  const message = typeof body?.error === 'string' && body.error ? body.error : `${r.status} ${text}`;
  throw new ApiError(
    r.status,
    message,
    typeof body?.error_code === 'string' ? body.error_code : null,
  );
}

async function get<T>(path: string): Promise<T> {
  const r = await fetch(path, { headers: { Accept: 'application/json' } });
  if (r.status === 401) throw new AuthRequiredError();
  if (!r.ok) await fail(r);
  return r.json() as Promise<T>;
}
async function send<T>(path: string, method: string, body?: unknown): Promise<T> {
  const r = await fetch(path, {
    method,
    headers: { 'Content-Type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (r.status === 401) throw new AuthRequiredError();
  if (!r.ok) await fail(r);
  return r.json() as Promise<T>;
}

const qs = (o: Record<string, string | undefined>) => {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(o)) if (v) p.set(k, v);
  const s = p.toString();
  return s ? `?${s}` : '';
};

export const fetchApi: CabinetApi = {
  health: () => get<HealthInfo>('/api/healthz'),
  today: () => get<TodayView>('/api/today'),
  domain: (id: DomainId) => get<DomainView>(`/api/domains/${id}`),
  ops: (filter?: { kind?: OpsKind; domain?: string }) => get<OpsFeed>(`/api/ops${qs({ kind: filter?.kind, domain: filter?.domain })}`),
  revertOp: (id: string) => send<{ ok: boolean }>(`/api/ops/${id}/revert`, 'POST'),
  usage: () => get<UsageView>('/api/usage'),
  usageRolling: () => get<UsageRollingView>('/api/usage/rolling'),
  perf: (hours?: number) => get<PerfView>(`/api/perf${qs({ hours: hours ? String(hours) : undefined })}`),
  memory: () => get<MemoryView>('/api/memory'),
  saveMemoryFile: (name: string, content: string) => send<{ ok: boolean }>(`/api/memory/${encodeURIComponent(name)}`, 'PUT', { content }),
  recall: (query: string) => get<RecallResponse>(`/api/recall${qs({ q: query })}`),
  chats: () => get<{ chats: ChatSummary[] }>('/api/chats'),
  createChat: () => send<{ id: string }>('/api/chats', 'POST', {}),
  deleteChat: (chatId: string) => send<{ ok: boolean }>(`/api/chats/${chatId}`, 'DELETE'),
  messages: (chatId: string) => get<{ messages: ChatMessage[] }>(`/api/chats/${chatId}/messages`),
  command: (intent: string) => send<{ chatId: string }>('/api/command', 'POST', { intent }),

  // The PUT/DELETE responses both carry the re-resolved view, so the caller
  // never has to guess what was stored or which source now wins.
  settings: () => get<{ settings: SettingView[] }>('/api/settings'),
  saveSetting: (key, value) => send<{ setting: SettingView }>(`/api/settings/${encodeURIComponent(key)}`, 'PUT', { value }),
  revertSetting: (key) => send<{ setting: SettingView }>(`/api/settings/${encodeURIComponent(key)}`, 'DELETE'),

  moneyAccounts: () => get<{ accounts: FinancialAccount[]; net_worth: NetWorth }>('/api/money/accounts'),
  moneySummary: (days?: number) => get<MoneySummary>(`/api/money/summary${qs({ days: days ? String(days) : undefined })}`),
  moneyTransactions: (opts?: { days?: number; limit?: number }) =>
    get<{ transactions: MoneyTransaction[] }>(
      `/api/money/transactions${qs({ days: opts?.days ? String(opts.days) : undefined, limit: opts?.limit ? String(opts.limit) : undefined })}`,
    ),
  moneyTrend: (days?: number) => get<MoneyTrend>(`/api/money/trend${qs({ days: days ? String(days) : undefined })}`),
  moneyCategories: (days?: number) =>
    get<{ categories: CategorySpend[] }>(`/api/money/categories${qs({ days: days ? String(days) : undefined })}`),
  moneyHoldings: () => get<{ holdings: Holding[] }>('/api/money/holdings'),
  moneySetAccountHidden: (accountId: number, hidden: boolean) =>
    send<{ ok: boolean; id: number; hidden: boolean }>(`/api/money/accounts/${accountId}`, 'PATCH', { hidden }),
};

export { ApiError, AuthRequiredError };
